import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {ContingentUsageControllerService, LimitDto} from '../api';

/**
 * Fachlicher Schlüssel einer Grenze.
 *
 * <p>Die bekannten Schlüssel stehen hier, weil das Frontend an genau zwei Stellen etwas über sie
 * wissen muss (Wortwahl im Singular/Plural und der Hinweis, wenn nichts mehr frei ist). Der
 * offene {@code string}-Anteil ist Absicht und kein Schludern: der Endpunkt liefert eine LISTE,
 * damit weitere Grenzen ohne UI-Änderung dazukommen können. Ein geschlossener Union würde dieses
 * Versprechen brechen – eine neue Grenze käme zur Laufzeit trotzdem an, die Typen würden dann nur
 * noch das Gegenteil behaupten.</p>
 */
export type LimitKey = 'storage' | 'myCheckTemplates' | (string & {});

/** Wie der Zahlenwert zu lesen ist – Bytes werden umgerechnet, Stückzahlen nicht. */
export type LimitUnit = 'BYTES' | 'COUNT';

/**
 * Woher die Grenze stammt.
 *
 * <p>Sichtbar wird davon nur, was vom Normalfall abweicht (siehe {@code sourceLabel} in der
 * Abo-Info): {@code PLAN} ist der Normalfall und braucht keine Erklärung.</p>
 */
export type LimitSource = 'OVERRIDE' | 'PLAN' | 'DEFAULT' | 'UNLIMITED';

/**
 * Eine mengenmäßige Grenze des Mandanten samt Verbrauch.
 *
 * <p>{@code limit} und {@code remaining} sind {@code null}, wenn es keine Grenze gibt. Der Wert 0
 * heißt dagegen „nichts mehr frei" – die beiden dürfen nie zusammengeworfen werden.</p>
 */
export interface TenantLimit {
  key: LimitKey;
  /** Vom Server vorgegeben; das Frontend erfindet keine eigene Beschriftung. */
  label: string;
  limit: number | null;
  used: number;
  remaining: number | null;
  unit: LimitUnit;
  source: LimitSource;
}

/**
 * Verbrauch eines Mandanten gegen seine mengenmäßigen Grenzen.
 *
 * <p>Alle Werte gelten für den ganzen Mandanten (Organisation oder persönliches Konto), nicht für
 * ein Projekt – die Grenzen hängen am Abo.</p>
 *
 * <p>Die Liste darf leer sein (kein Zugriff, kein Kontingent). Dann zeigt die Abo-Info den Block
 * gar nicht an, statt eine leere Überschrift stehen zu lassen.</p>
 */
export interface ContingentUsage {
  limits: TenantLimit[];
}

/**
 * Holt den Verbrauchsstand des Mandanten.
 *
 * <p>Ruft den generierten Client auf, übersetzt dessen Modell aber in die Typen oben – und das ist
 * der eigentliche Zweck dieser Klasse. Der Generator macht jedes Feld optional
 * ({@code limit?: number}), weil OpenAPI keine Nicht-Null-Zusage kennt. Damit wäre in der ganzen
 * Oberfläche nicht mehr unterscheidbar, was „unbegrenzt" (bewusst {@code null}) und was „hat der
 * Server nicht geliefert" ({@code undefined}) bedeutet – genau die Unterscheidung, an der die
 * Anzeige hängt.</p>
 */
@Injectable({providedIn: 'root'})
export class ContingentUsageService {
  private readonly api = inject(ContingentUsageControllerService);

  /**
   * @param organisationId Organisation, deren Abo angezeigt wird; ohne Angabe das persönliche Konto.
   */
  getUsage(organisationId?: number): Observable<ContingentUsage> {
    return this.api.getContingentUsage(organisationId).pipe(
      map(dto => ({limits: (dto?.limits ?? []).map(limit => this.toLimit(limit))})),
    );
  }

  private toLimit(limit: LimitDto): TenantLimit {
    return {
      key: limit.key ?? '',
      label: limit.label ?? '',
      // `?? null`, nicht `?? 0`: eine fehlende Grenze ist keine Grenze von null.
      limit: limit.limit ?? null,
      used: limit.used ?? 0,
      remaining: limit.remaining ?? null,
      unit: (limit.unit as LimitUnit) ?? 'COUNT',
      source: (limit.source as LimitSource) ?? 'DEFAULT',
    };
  }
}
